import { logError, logWarn } from '@ptek/client-logger';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { EVENT_PREFIX } from './constants/events';
import { BrandThemeProvider } from './context/BrandThemeContext';
import { WidgetContextProvider, } from './context/WidgetContext';
import { WidgetEventsProvider } from './context/WidgetEventsContext';
import { WidgetContextManager } from './managers/WidgetContextManager';
import { WidgetDOMManager } from './managers/WidgetDOMManager';
import { WidgetEventManager } from './managers/WidgetEventManager';
import { brandToWidgetTheme, deriveBrandTokens } from './styles/brandUtils';
import { defaultTheme, } from './types';
import { SizeResolver } from './utils/SizeResolver';
/**
 * Widget Events Reference:
 *
 * System Events (emitted by widget shell, no prefix):
 * - 'mounted': Emitted when the widget is successfully mounted in the DOM
 * - 'ready': Emitted when the React app is rendered and ready for interaction
 * - 'show': Emitted when the widget becomes visible
 * - 'hide': Emitted when the widget becomes hidden
 * - 'unmounted': Emitted when the widget is removed from the DOM
 * - 'close': Internal event emitted when the close button is clicked
 * - 'fullscreenEntered': Emitted when the widget enters fullscreen mode
 * - 'fullscreenExited': Emitted when the widget exits fullscreen mode
 * - 'collapsed': Emitted when the widget is collapsed to compact mode
 * - 'expanded': Emitted when the widget is expanded to full mode
 * - 'contextUpdated': Emitted when the widget context is updated
 * - 'error': Emitted when an error occurs during rendering or operation
 *
 * App Events (emitted to React app with 'host:' prefix):
 * - All fullscreen events are also emitted to the React app
 * - Custom events emitted via the public emit() method
 *
 * Note: The widget uses helper functions for clarity:
 * - emitSystemEvent(): For internal widget shell events
 * - emitToApp(): For events sent to the React app
 * - emitFullscreenEvent(): For events that need to go to both system and app
 */
/**
 * Base widget class providing core functionality for embeddable widgets
 */
export class BaseWidget {
    /**
     * Creates a new instance of BaseWidget
     * @param config Widget configuration
     */
    constructor(config) {
        Object.defineProperty(this, "domManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contextManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "eventManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "root", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_isMounted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "_isVisible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "theme", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "brandConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "derivedBrandTokens", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lifecycleHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        if (!config.appComponent) {
            throw new Error('appComponent is required in WidgetConfig');
        }
        this.config = this.mergeWithDefaults(config);
        this.brandConfig = config.brand;
        // Derive theme: brand config takes precedence, then explicit theme, then defaults
        // deriveBrandTokens() is called ONCE here and reused for both shell and React
        if (config.brand) {
            this.derivedBrandTokens = deriveBrandTokens(config.brand);
            const brandTheme = brandToWidgetTheme(this.derivedBrandTokens);
            // Allow explicit theme overrides on top of brand-derived theme
            this.theme = { ...brandTheme, ...(config.theme || {}) };
        }
        else {
            this.derivedBrandTokens = deriveBrandTokens({});
            this.theme = { ...defaultTheme, ...(config.theme || {}) };
        }
        this.contextManager = new WidgetContextManager(this.config.initialContext || {});
        this.eventManager = new WidgetEventManager();
        this.domManager = new WidgetDOMManager(this.config, this.theme, () => this.handleCloseRequest(), () => this.toggleFullscreen(), () => this.toggleExpandCollapse());
        // Set up event listeners for fullscreen requests from React apps
        this.setupFullscreenEventListeners();
        this.setupResizeEventListeners();
        this.setupCloseEventListeners();
    }
    /**
     * Sets up event listeners for fullscreen requests from React apps
     * @private
     */
    setupFullscreenEventListeners() {
        this.on('requestEnterFullscreen', () => {
            this.enterFullscreen();
        });
        this.on('requestExitFullscreen', () => {
            this.exitFullscreen();
        });
        this.on('requestToggleFullscreen', () => {
            this.toggleFullscreen();
        });
        this.on('requestCollapse', () => {
            this.collapse();
        });
        this.on('requestExpand', () => {
            this.expand();
        });
        this.on('requestToggleExpandCollapse', () => {
            this.toggleExpandCollapse();
        });
        // Handle requests for current expand/collapse state
        this.on('requestExpandCollapseState', () => {
            if (this._isMounted) {
                const isExpanded = this.domManager.isExpanded();
                this.emitToApp('expandCollapseState', { isExpanded });
            }
        });
    }
    /**
     * Sets up event listeners for resize requests from React apps
     */
    setupResizeEventListeners() {
        this.on('requestResize', (size) => {
            this.setSize(size);
        });
    }
    /**
     * Sets up event listeners for close requests from React apps
     * @private
     */
    setupCloseEventListeners() {
        // Listen for confirmClose event from React app to actually close the widget
        this.on('confirmClose', () => {
            this.handleCloseRequest();
        });
    }
    /**
     * Gets the original configuration used to create this widget
     * @returns The widget configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Merges provided config with defaults
     * @param config User provided configuration
     * @returns Complete configuration with defaults
     * @private
     */
    mergeWithDefaults(config) {
        return {
            id: config.id,
            title: config.title || 'Widget',
            appComponent: config.appComponent,
            // Initial State
            initialPosition: config.initialPosition || { x: 20, y: 20 },
            initialSize: config.initialSize || { width: 300, height: 400 },
            startFullscreen: config.startFullscreen || false,
            startCollapsed: config.startCollapsed || false,
            initialContext: config.initialContext || {},
            // Styling & Constraints
            theme: config.theme,
            brand: config.brand,
            sizeConstraints: {
                minWidth: 200,
                minHeight: 150,
                ...config.sizeConstraints,
            },
            // Capabilities
            capabilities: {
                draggable: config.capabilities?.draggable !== false,
                resizable: config.capabilities?.resizable !== false,
                canToggleFullscreen: config.capabilities?.canToggleFullscreen || false,
                allowClose: config.capabilities?.allowClose || false,
                ...config.capabilities,
            },
        };
    }
    /**
     * Gets the current position of the widget
     * @returns Position coordinates
     */
    getPosition() {
        return this.domManager.getPosition();
    }
    /**
     * Gets the current size of the widget
     * @returns Size dimensions
     */
    getSize() {
        return this.domManager.getSize();
    }
    /**
     * Sets the position of the widget
     * @param position New position coordinates
     * @returns This instance for chaining
     */
    setPosition(position) {
        if (!this._isMounted)
            return this;
        this.domManager.setPosition(position);
        return this;
    }
    /**
     * Sets the size of the widget
     * @param size New size dimensions
     * @returns This instance for chaining
     */
    setSize(size) {
        if (!this._isMounted)
            return this;
        const viewport = SizeResolver.getViewportSize();
        const resolved = SizeResolver.resolve(size, viewport);
        this.domManager.setSize(resolved);
        return this;
    }
    /**
     * Mounts the widget in the DOM
     * @returns This instance for chaining
     */
    mount() {
        if (this._isMounted)
            return this;
        this.domManager.mount();
        this.renderApp();
        this._isMounted = true;
        this._isVisible = true;
        this.emitSystemEvent('mounted', { id: this.domManager.getContainerId() });
        // Handle startFullscreen configuration
        if (this.config.startFullscreen) {
            setTimeout(() => {
                this.enterFullscreen(true); // Skip transition for initial fullscreen state
            }, 0);
        }
        return this;
    }
    /**
     * Renders the React application inside the widget
     * @private
     */
    renderApp() {
        try {
            const reactRoot = this.domManager.getReactRoot();
            if (!reactRoot) {
                logError('BaseWidget', 'React root element not found');
                return;
            }
            if (!this.root) {
                this.root = createRoot(reactRoot);
            }
            const AppComponent = this.config.appComponent;
            const appElement = React.createElement(AppComponent);
            // Create lifecycle handler callback that the React app can call
            const onLifecycleEvent = (event) => {
                if (this.lifecycleHandler) {
                    try {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return this.lifecycleHandler(event);
                    }
                    catch (error) {
                        logWarn('BaseWidget', `Widget lifecycle handler threw error for ${event}:`, error);
                        return true;
                    }
                }
                return true; // Default to allowing the action
            };
            // Note: StrictMode is disabled because it causes memory leaks with AuthClient
            // in development. StrictMode's double-mounting creates multiple AuthClient instances
            // but cleanup effects get mixed up, leaving some timers running after unmount.
            // Since widgets are deployed in production without StrictMode, this is acceptable.
            //
            // Provider hierarchy (outermost to innermost):
            // 1. BrandThemeProvider - Brand tokens for theming (pre-derived, no redundant computation)
            // 2. WidgetContextProvider - Widget context data
            // 3. WidgetEventsProvider - Event communication
            // 4. AppComponent - The actual widget application
            this.root.render(React.createElement(BrandThemeProvider, {
                tokens: this.derivedBrandTokens,
                rawConfig: this.brandConfig,
                children: React.createElement(WidgetContextProvider, {
                    initialContext: this.contextManager.getContext(),
                    contextUpdateKey: this.contextManager.getContextUpdateKey(),
                    onLifecycleEvent,
                    onRegisterLifecycleHandler: (handler) => {
                        this.lifecycleHandler = handler;
                    },
                    children: React.createElement(WidgetEventsProvider, {
                        eventManager: this.eventManager,
                        children: appElement,
                    }),
                }),
            }));
            // Emit ready event and initial state after React app is rendered
            setTimeout(() => {
                this.emitSystemEvent('ready', { id: this.domManager.getContainerId() });
                // Emit initial expand/collapse state after ready event
                const isExpanded = this.domManager.isExpanded();
                this.emitToApp('expandCollapseState', { isExpanded });
            }, 50); // Small delay to ensure React is fully rendered
        }
        catch (error) {
            logError('BaseWidget', 'Failed to render React app:', error);
            this.emitSystemEvent('error', {
                message: 'Failed to render React application',
                error: error instanceof Error ? error : new Error(String(error)),
            });
        }
    }
    /**
     * Shows the widget
     * @returns This instance for chaining
     */
    show() {
        if (this._isMounted) {
            this.domManager.show();
            this._isVisible = true;
            this.emitSystemEvent('show', { id: this.domManager.getContainerId() });
        }
        return this;
    }
    /**
     * Hides the widget while checking with React app first
     * @returns This instance for chaining
     */
    hide() {
        if (this._isMounted && this.canPerformLifecycleAction('beforeHide')) {
            this.domManager.hide();
            this._isVisible = false;
            this.emitSystemEvent('hide', { id: this.domManager.getContainerId() });
        }
        return this;
    }
    /**
     * Checks if the widget is currently visible
     * @returns True if the widget is visible
     */
    isVisible() {
        return this._isVisible;
    }
    /**
     * Gets the current lifecycle state of the widget
     * @returns The current lifecycle state
     */
    getLifecycleState() {
        if (!this._isMounted)
            return 'unmounted';
        if (!this._isVisible)
            return 'mounted-hidden';
        return 'mounted-visible';
    }
    /**
     * Unmounts the widget from the DOM
     * @returns This instance for chaining
     */
    unmount() {
        if (!this.canPerformLifecycleAction('beforeUnmount')) {
            return this;
        }
        return this.doUnmount();
    }
    /**
     * Internal unmount method that actually performs the unmounting
     * @private
     */
    doUnmount() {
        if (this._isMounted && this.root) {
            this.root.unmount();
            this.root = null;
            this.domManager.unmount();
            this._isMounted = false;
            this._isVisible = false;
            this.emitSystemEvent('unmounted', {
                id: this.domManager.getContainerId(),
            });
        }
        return this;
    }
    /**
     * Pushes the current context manager state to the React WidgetContextProvider
     * via the window global callback registered by the provider.
     * @private
     */
    syncContextToReact() {
        const key = this.contextManager.getContextUpdateKey();
        const win = window;
        const updateFn = win[key];
        if (typeof updateFn === 'function') {
            updateFn(this.contextManager.getContext());
        }
    }
    /**
     * Updates the application context
     * @param data New context data
     * @param options Configuration options
     * @returns This instance for chaining
     */
    setContext(data, options) {
        this.contextManager.setContext(data, options);
        this.syncContextToReact();
        this.emitSystemEvent('contextUpdated', {
            context: this.contextManager.getContext(),
        });
        return this;
    }
    /**
     * Removes a key from the application context
     * @param key The key to remove
     * @returns This instance for chaining
     */
    removeContext(key) {
        this.contextManager.removeContext(key);
        this.syncContextToReact();
        this.emitSystemEvent('contextUpdated', {
            context: this.contextManager.getContext(),
        });
        return this;
    }
    /**
     * Clears the entire application context
     * @returns This instance for chaining
     */
    clearContext() {
        this.contextManager.clearContext();
        this.syncContextToReact();
        this.emitSystemEvent('contextUpdated', {
            context: this.contextManager.getContext(),
        });
        return this;
    }
    /**
     * Gets the current application context
     * @returns The current context
     */
    getContext() {
        return this.contextManager.getContext();
    }
    /**
     * Subscribes to widget events from the app
     * @param eventName Name of the event to subscribe to
     * @param handler Event handler function
     * @returns This instance for chaining
     */
    on(eventName, handler) {
        this.eventManager.on(`${EVENT_PREFIX.APP}${eventName}`, handler);
        return this;
    }
    /**
     * Unsubscribes from widget events from the app
     * @param eventName Name of the event to unsubscribe from
     * @param handler Event handler function to remove
     * @returns This instance for chaining
     */
    off(eventName, handler) {
        this.eventManager.off(`${EVENT_PREFIX.APP}${eventName}`, handler);
        return this;
    }
    /**
     * Subscribes to a widget event from the app that will automatically be removed after first trigger
     * @param eventName Name of the event to subscribe to once
     * @param handler Event handler function
     * @returns This instance for chaining
     */
    once(eventName, handler) {
        this.eventManager.once(`${EVENT_PREFIX.APP}${eventName}`, handler);
        return this;
    }
    /**
     * Emits an event to the widget application
     * @param eventName Name of the event to emit
     * @param eventData Data to pass with the event
     * @returns This instance for chaining
     */
    emit(eventName, eventData) {
        this.emitToApp(eventName, eventData);
        return this;
    }
    /**
     * Removes all handlers for a specific event
     * @param eventName The event to clear handlers for
     * @returns This instance for chaining
     */
    clearEvent(eventName) {
        this.eventManager.clearEvent(`${EVENT_PREFIX.APP}${eventName}`);
        return this;
    }
    /**
     * Removes all event handlers
     * @returns This instance for chaining
     */
    clearAllEvents() {
        this.eventManager.clearAllEvents();
        return this;
    }
    /**
     * Emits a system event (internal widget shell event)
     * @param eventName Name of the system event to emit
     * @param data Event data
     * @returns This instance for chaining
     * @private
     */
    emitSystemEvent(eventName, data) {
        this.eventManager.emit(eventName, data);
        return this;
    }
    /**
     * Emits an event to the React app
     * @param eventName Name of the event to emit to the app
     * @param data Event data
     * @returns This instance for chaining
     * @private
     */
    emitToApp(eventName, data) {
        this.eventManager.emit(`${EVENT_PREFIX.HOST}${eventName}`, data);
        return this;
    }
    /**
     * Emits an event on the app channel that external `on()` listeners subscribe
     * to, so a subclass can signal the host before (or instead of) mounting the
     * React app. Symmetric to `on()`: both use the `app:` prefix, whereas the
     * public `emit()` targets the `host:` channel the React app listens on.
     * @param eventName Name of the event to emit to host listeners
     * @param data Event data
     * @returns This instance for chaining
     */
    emitToHostListeners(eventName, data) {
        this.eventManager.emit(`${EVENT_PREFIX.APP}${eventName}`, data);
        return this;
    }
    /**
     * Emits both system and app events for fullscreen state changes
     * @param eventName The base event name (e.g., 'fullscreenEntered')
     * @param data Event data
     * @private
     */
    emitFullscreenEvent(eventName, data) {
        this.emitSystemEvent(eventName, data);
        this.emitToApp(eventName, data);
    }
    /**
     * Checks if the widget is currently mounted in the DOM
     * @returns True if the widget is mounted
     */
    isMounted() {
        return this._isMounted;
    }
    /**
     * Enters fullscreen mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async enterFullscreen(skipTransition = false) {
        if (!this._isMounted)
            return false;
        const result = await this.domManager.enterFullscreen(skipTransition);
        if (result) {
            this.emitFullscreenEvent('fullscreenEntered', {
                id: this.domManager.getContainerId(),
            });
        }
        return result;
    }
    /**
     * Exits fullscreen mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async exitFullscreen() {
        if (!this._isMounted)
            return false;
        const result = await this.domManager.exitFullscreen();
        if (result) {
            this.emitFullscreenEvent('fullscreenExited', {
                id: this.domManager.getContainerId(),
            });
        }
        return result;
    }
    /**
     * Toggles fullscreen mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async toggleFullscreen() {
        if (!this._isMounted)
            return false;
        const wasFullscreen = this.isFullscreen();
        const result = await this.domManager.toggleFullscreen();
        if (result) {
            if (wasFullscreen) {
                this.emitFullscreenEvent('fullscreenExited', {
                    id: this.domManager.getContainerId(),
                });
            }
            else {
                this.emitFullscreenEvent('fullscreenEntered', {
                    id: this.domManager.getContainerId(),
                });
            }
        }
        return result;
    }
    /**
     * Checks if the widget is currently in fullscreen mode
     * @returns True if the widget is in fullscreen mode
     */
    isFullscreen() {
        if (!this._isMounted)
            return false;
        return this.domManager.isFullscreen();
    }
    /**
     * Collapses the widget to compact mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async collapse() {
        if (!this._isMounted)
            return false;
        const result = await this.domManager.collapse();
        if (result) {
            this.emitSystemEvent('collapsed', {
                id: this.domManager.getContainerId(),
            });
            this.emitToApp('collapsed', {
                id: this.domManager.getContainerId(),
            });
        }
        return result;
    }
    /**
     * Expands the widget to full mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async expand() {
        if (!this._isMounted)
            return false;
        const result = await this.domManager.expand();
        if (result) {
            this.emitSystemEvent('expanded', {
                id: this.domManager.getContainerId(),
            });
            this.emitToApp('expanded', {
                id: this.domManager.getContainerId(),
            });
        }
        return result;
    }
    /**
     * Toggles expand/collapse state
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async toggleExpandCollapse() {
        if (!this._isMounted)
            return false;
        const wasExpanded = this.isExpanded();
        const result = await this.domManager.toggleExpandCollapse();
        if (result) {
            if (wasExpanded) {
                this.emitSystemEvent('collapsed', {
                    id: this.domManager.getContainerId(),
                });
                this.emitToApp('collapsed', {
                    id: this.domManager.getContainerId(),
                });
            }
            else {
                this.emitSystemEvent('expanded', {
                    id: this.domManager.getContainerId(),
                });
                this.emitToApp('expanded', {
                    id: this.domManager.getContainerId(),
                });
            }
        }
        return result;
    }
    /**
     * Checks if the widget is currently expanded
     * @returns True if the widget is expanded
     */
    isExpanded() {
        if (!this._isMounted)
            return true; // Default to expanded when not mounted
        return this.domManager.isExpanded();
    }
    /**
     * Sets custom sizes for expand/collapse modes
     * @param compactSize Size when collapsed
     * @param expandedSize Size when expanded
     * @returns This instance for chaining
     */
    setExpandCollapseSizes(compactSize, expandedSize) {
        if (this._isMounted) {
            this.domManager.setExpandCollapseSizes(compactSize, expandedSize);
        }
        return this;
    }
    /**
     * Destroys the widget and cleans up all resources
     * @returns This instance for chaining
     */
    destroy() {
        this.unmount();
        this.eventManager.clearAllEvents();
        this.emitSystemEvent('destroyed', { id: this.domManager.getContainerId() });
        return this;
    }
    handleCloseRequest() {
        if (this.canPerformLifecycleAction('beforeUnmount')) {
            this.doUnmount();
        }
    }
    /**
     * Checks if a lifecycle action can be performed by asking the React app
     * @param event The lifecycle event to check
     * @returns true if action is allowed, false if prevented
     * @private
     */
    canPerformLifecycleAction(event) {
        if (this.lifecycleHandler) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return this.lifecycleHandler(event);
            }
            catch (error) {
                logWarn('BaseWidget', `Widget lifecycle handler threw error for ${event}:`, error);
                return true;
            }
        }
        return true;
    }
}
//# sourceMappingURL=BaseWidget.js.map